
import {defineComponent} from "vue";
import {CbStructureMixin} from "@/components/layouts/content-pages/wysiwyg/structures/CbStructureMixin";
import BaseSpinner from "@/components/UI/Bootstrap/BaseSpinner.vue";
import InputSelect from "@/components/UI/InputSelect.vue";
import {CbJsonCareCalendarModel} from "@/models/api/pharma-cms-content-block/CbJsonCareCalendarModel";
import {CodeDescriptionRestDto} from "@/api/care-calendar-widget-selection/models";
import {WidgetSelectionRestService} from "@/services/rest/care-calendar-widget-selection/WidgetSelectionRestService";
import {DpEvent} from "@/models/DpEvent";
import {CreateNormalContentBlockPayload} from "@/models/payload/CreateNormalContentBlockPayload";
import {FindNormalContentBlockPayload} from "@/models/payload/FindNormalContentBlockPayload";
import {SaveNormalContentBlockPayload} from "@/models/payload/SaveNormalContentBlockPayload";
import {JsonContentRestDtoModel} from "@/models/api/pharma-cms-content-block/JsonContentRestDtoModel";
import {ContentBlockEndpointsApiService} from "@/main";
import {AbilityContext} from "@/context/AbilityContext";
import {CustomerContext} from "@/context/CustomerContext";

export default defineComponent({
  name: "CbJsonCareCalendar",
  extends: CbStructureMixin,
  components: {
    BaseSpinner,
    InputSelect,
  },
  data: () => ({
    contentBlock: CbJsonCareCalendarModel.createWithDefaults(),
    widgetOptions: [] as CodeDescriptionRestDto[],
    hasSelectableWidgets: false as boolean,
    hasLicense: false as boolean,
    mailToTarget: 'mail@digital-pharma.be',
  }),
  computed: {
    requestLicenseMailToLink(): string {
      const subject = encodeURIComponent(this.$t('cms.careCalendar_requestLicenseMailSubject'));
      const body = encodeURIComponent(
        this.$t('cms.careCalendar_requestLicenseMailBody', {customerCode: CustomerContext.getCustomerCode()})
      );

      return `mailto:${this.mailToTarget}?subject=${subject}&body=${body}`;
    },
    isSelectionUnresolved(): boolean {
      const widgetCode = this.contentBlock.widget_code;
      if (!widgetCode) {
        return false;
      }

      return !this.widgetOptions.some((option: CodeDescriptionRestDto) => option.code === widgetCode);
    },
  },
  methods: {
    async create(payload: CreateNormalContentBlockPayload): Promise<string> {
      const apiPayload = JsonContentRestDtoModel.createWithDefaults();
      if (payload.cb != null) {
        apiPayload.content = payload.cb;
      }
      const response = await ContentBlockEndpointsApiService.createNormalContentBlockJson(apiPayload as JsonContentRestDtoModel, payload.type);

      return response.data.id as string;
    },
    async find(payload: FindNormalContentBlockPayload): Promise<CbJsonCareCalendarModel> {
      const response = await ContentBlockEndpointsApiService.getNormalContentBlockJson(payload.type, payload.id);

      const responseData = response.data;
      let careCalendar = CbJsonCareCalendarModel.createWithDefaults();
      if (responseData.content != null) {
        careCalendar = Object.assign(CbJsonCareCalendarModel.createWithDefaults(), responseData.content);
      }

      return careCalendar;
    },
    async save(payload: SaveNormalContentBlockPayload): Promise<void> {
      const apiPayload = JsonContentRestDtoModel.createWithDefaults();
      if (payload.cb != null) {
        apiPayload.content = payload.cb;
      }
      await ContentBlockEndpointsApiService.updateNormalContentBlockJson(apiPayload as JsonContentRestDtoModel, payload.type, payload.id);
    },
    async beforeReloadContent(event: DpEvent): Promise<void> {
      this.hasLicense = AbilityContext.isAuthorizedForFeature('WIDGET_MGMT');

      if (!this.hasLicense) {
        event.stopPropagation();
        return;
      }

      this.widgetOptions = await WidgetSelectionRestService.getInstance().findSelectableBookingWidgets();
      this.hasSelectableWidgets = this.widgetOptions.length > 0;

      if (!this.hasSelectableWidgets) {
        event.stopPropagation();
      }
    },
    getDefaultCbModel(): CbJsonCareCalendarModel {
      return CbJsonCareCalendarModel.createWithDefaults();
    },
    pasteCbMetadata(): void {
      // Not supported for this block type, see BR-05.
    },
  }
});
